import { useEffect, useMemo, useState, type ReactNode } from "react";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { AppLink } from '../../components/layout/AppLink';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { apiError } from "../../lib/apiError";
import { formatDate } from "../../lib/format";
import type { DashboardStats, StatsCountItem } from "../../types";

type SeasonScope = "all" | "active" | "archived";

const EXPLANATIONS: Record<string, string> = {
  herd_total:
    "Liczba aktywnych zwierząt w stadzie. Sprzedane, padłe i po uboju są w archiwum.",
  avg_age:
    "Średnia wieku zwierząt z podaną datą urodzenia. Bez daty — pomijane w średniej.",
  under_over:
    "Podział zwierząt z datą urodzenia: młodsze niż 1 rok oraz w wieku 1 roku i więcej.",
  litter: "Średnia liczba jagniąt na jeden wykot (we wszystkich sezonach).",
  coverage: "Odsetek owiec w stanówce z odnotowanym pokryciem (średnia z sezonów).",
  exits: "Zwierzęta usunięte ze stada: sprzedaż, padnięcie lub ubój.",
  tags: "Pula oficjalnych kolczyków: wolne vs przypisane do owiec lub jagniąt.",
};

function formatYears(value: number | null | undefined): string {
  if (value == null) {
    return "—";
  }
  return `${value.toLocaleString("pl-PL", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} lat`;
}

function formatPct(value: number | null | undefined): string {
  if (value == null) {
    return "—";
  }
  return `${value.toLocaleString("pl-PL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;
}

function formatNum(value: number | null | undefined): string {
  if (value == null) {
    return "—";
  }
  return value.toLocaleString("pl-PL", { maximumFractionDigits: 2 });
}

function StatHint({ id }: { id: string }) {
  const text = EXPLANATIONS[id];
  if (!text) {
    return null;
  }
  return (
    <details className="stat-hint">
      <summary>Co to oznacza?</summary>
      <p>{text}</p>
    </details>
  );
}

function BarList({
  items,
  empty,
  tone = "green",
}: {
  items: StatsCountItem[];
  empty: string;
  tone?: "green" | "amber" | "rose" | "slate";
}) {
  if (items.length === 0) {
    return <p className="muted">{empty}</p>;
  }
  const max = Math.max(...items.map((item) => item.count), 1);
  return (
    <ul className={`stats-bars stats-bars--${tone}`} aria-label="Rozkład">
      {items.map((item) => (
        <li key={item.key} className="stats-bars__row">
          <div className="stats-bars__meta">
            <span>{item.label}</span>
            <span className="stats-bars__value">
              {item.count}
              {item.pct > 0 ? (
                <span className="muted"> · {item.pct}%</span>
              ) : null}
            </span>
          </div>
          <div className="stats-bars__track">
            <div
              className="stats-bars__fill"
              style={{ width: `${Math.max(6, (100 * item.count) / max)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function StatSection({
  title,
  summary,
  tone,
  open = false,
  children,
  actions,
}: {
  title: string;
  summary?: string;
  tone: "herd" | "repro" | "exits" | "tags";
  open?: boolean;
  children: ReactNode;
  actions?: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(open);
  return (
    <details
      className={`stats-section stats-section--${tone}`}
      open={isOpen}
      onToggle={(event) => {
        setIsOpen((event.currentTarget as HTMLDetailsElement).open);
      }}
    >
      <summary className="stats-section__summary">
        <span className="stats-section__title-wrap">
          <span className="stats-section__accent" aria-hidden="true" />
          <span>
            <span className="stats-section__title">{title}</span>
            {summary ? <span className="stats-section__lede">{summary}</span> : null}
          </span>
        </span>
        <span className="stats-section__chevron" aria-hidden="true" />
      </summary>
      {actions ? <div className="stats-section__actions">{actions}</div> : null}
      <div className="stats-section__body">{children}</div>
    </details>
  );
}

function MiniStat({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: ReactNode;
  note?: string;
  tone?: "default" | "ok" | "warn" | "danger" | "info";
}) {
  return (
    <article className={`stats-mini${tone && tone !== "default" ? ` stats-mini--${tone}` : ""}`}>
      <p className="stats-mini__label">{label}</p>
      <p className="stats-mini__value">{value}</p>
      {note ? <p className="stats-mini__note">{note}</p> : null}
    </article>
  );
}

function DetailBlock({
  title,
  open = false,
  children,
}: {
  title: string;
  open?: boolean;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(open);
  return (
    <details
      className="stats-detail"
      open={isOpen}
      onToggle={(event) => {
        setIsOpen((event.currentTarget as HTMLDetailsElement).open);
      }}
    >
      <summary>{title}</summary>
      <div className="stats-detail__body">{children}</div>
    </details>
  );
}

export function StatisticsPage() {
  const [data, setData] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seasonScope, setSeasonScope] = useState<SeasonScope>("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await api.GET("/api/dashboard/stats");
      if (cancelled) {
        return;
      }
      if (result.error) {
        setError(apiError(result.error, "Nie udało się wczytać statystyk."));
        return;
      }
      setError(null);
      setData(result.data as DashboardStats);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const lambingSeasons = useMemo(() => {
    const rows = data?.births.by_season ?? [];
    if (seasonScope === "active") {
      return rows.filter((row) => !row.archived);
    }
    if (seasonScope === "archived") {
      return rows.filter((row) => row.archived);
    }
    return rows;
  }, [data, seasonScope]);

  const breedingSeasons = useMemo(() => {
    const rows = data?.breeding.by_season ?? [];
    if (seasonScope === "active") {
      return rows.filter((row) => !row.archived);
    }
    if (seasonScope === "archived") {
      return rows.filter((row) => row.archived);
    }
    return rows;
  }, [data, seasonScope]);

  const lambingView = useMemo(() => {
    const seasons = lambingSeasons;
    const eventCount = seasons.reduce((sum, row) => sum + row.event_count, 0);
    const lambCount = seasons.reduce((sum, row) => sum + row.offspring_count, 0);
    const femaleLambs = seasons.reduce((sum, row) => sum + row.female_offspring, 0);
    const maleLambs = seasons.reduce((sum, row) => sum + row.male_offspring, 0);
    const missingOfficial = seasons.reduce(
      (sum, row) => sum + row.missing_official_tags,
      0,
    );
    // Średni miot: ważony liczbą wykotów w sezonie.
    const litterSum = seasons.reduce((sum, row) => {
      if (row.avg_litter_size == null || row.event_count <= 0) {
        return sum;
      }
      return sum + row.avg_litter_size * row.event_count;
    }, 0);
    const avgLitter =
      eventCount > 0 ? Math.round((litterSum / eventCount) * 100) / 100 : null;
    return {
      seasonCount: seasons.length,
      eventCount,
      lambCount,
      femaleLambs,
      maleLambs,
      missingOfficial,
      avgLitter,
    };
  }, [lambingSeasons]);

  const breedingView = useMemo(() => {
    const seasons = breedingSeasons;
    const eweCount = seasons.reduce((sum, row) => sum + row.dam_count, 0);
    const ramCount = seasons.reduce((sum, row) => sum + row.sire_count, 0);
    const matingCount = seasons.reduce((sum, row) => sum + row.mated_count, 0);
    const coverageValues = seasons
      .map((row) => row.mating_coverage_pct)
      .filter((value): value is number => value != null);
    const avgCoverage =
      coverageValues.length > 0
        ? Math.round(
            (coverageValues.reduce((sum, value) => sum + value, 0) /
              coverageValues.length) *
              10,
          ) / 10
        : null;
    return {
      seasonCount: seasons.length,
      eweCount,
      ramCount,
      matingCount,
      avgCoverage,
    };
  }, [breedingSeasons]);

  const scopeLabel =
    seasonScope === "active"
      ? "aktywne sezony"
      : seasonScope === "archived"
        ? "archiwum"
        : "wszystkie sezony";

  function setScope(value: SeasonScope) {
    setSeasonScope(value);
  }

  return (
    <main className="page stats-page" data-testid="statistics-page">
      <header className="page__header page__header--row">
        <div>
          <p className="page__kicker">Pulpit</p>
          <h1>Statystyki stada</h1>
          <p className="muted">
            Podsumowanie konta
            {data ? ` · stan na ${formatDate(String(data.generated_at))}` : ""}
          </p>
        </div>
        <AppLink href="/" className="btn btn--ghost">
          ← Panel główny
        </AppLink>
      </header>

      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      {!data && !error ? <p className="muted">Wczytywanie statystyk…</p> : null}

      {data ? (
        <div className="stats-layout">
          <section className="stats-overview" aria-label="Najważniejsze liczby">
            <article className="stats-hero stats-hero--herd">
              <p className="stats-hero__label">W stadzie</p>
              <p className="stats-hero__value">{data.herd.total}</p>
              <p className="stats-hero__note">
                {data.herd.females} samic · {data.herd.males} samców
              </p>
              <StatHint id="herd_total" />
            </article>
            <article className="stats-hero stats-hero--age">
              <p className="stats-hero__label">Średni wiek</p>
              <p className="stats-hero__value">
                {formatYears(data.herd.age.average_years)}
              </p>
              <p className="stats-hero__note">
                poniżej roku: {data.herd.age.under_1_year} · od roku:{" "}
                {data.herd.age.over_1_year}
              </p>
              <StatHint id="avg_age" />
            </article>
            <article className="stats-hero stats-hero--repro">
              <p className="stats-hero__label">Jagnięta łącznie</p>
              <p className="stats-hero__value">{data.births.offspring_count}</p>
              <p className="stats-hero__note">
                {data.births.event_count} wykotów · śr. miot{" "}
                {formatNum(data.births.avg_litter_size)}
              </p>
              <StatHint id="litter" />
            </article>
            <article className="stats-hero stats-hero--exits">
              <p className="stats-hero__label">Odejścia</p>
              <p className="stats-hero__value">{data.exits.total}</p>
              <p className="stats-hero__note">
                sprzedane {data.exits.sold} · padłe {data.exits.dead} · ubój{" "}
                {data.exits.slaughtered}
              </p>
              <StatHint id="exits" />
            </article>
          </section>

          <StatSection
            title="Stado aktywne"
            summary="Płeć, wiek, program i rasy"
            tone="herd"
            open
          >
            <div className="stats-mini-grid">
              <MiniStat label="Samice" value={data.herd.females} tone="ok" />
              <MiniStat label="Samce" value={data.herd.males} tone="info" />
              <MiniStat
                label="Poniżej roku"
                value={data.herd.age.under_1_year}
                note="z datą urodzenia"
              />
              <MiniStat
                label="Powyżej roku"
                value={data.herd.age.over_1_year}
                note="z datą urodzenia"
              />
            </div>
            <p className="stats-meta muted">
              Mediana: {formatYears(data.herd.age.median_years)}
              {data.herd.age.without_birth_date > 0
                ? ` · bez daty urodzenia: ${data.herd.age.without_birth_date}`
                : ""}
              {data.herd.missing_mother_tag > 0
                ? ` · bez matki: ${data.herd.missing_mother_tag}`
                : ""}
            </p>
            <StatHint id="under_over" />

            <div className="stats-detail-stack">
              <DetailBlock title="Rozkład wieku" open>
                <BarList
                  items={data.herd.age.buckets}
                  empty="Brak zwierząt z datą urodzenia."
                  tone="green"
                />
              </DetailBlock>
              <DetailBlock title="Płeć">
                <BarList items={data.herd.by_sex} empty="Brak danych." tone="green" />
              </DetailBlock>
              <DetailBlock title="Program / licencja">
                <BarList
                  items={data.herd.by_program}
                  empty="Brak danych."
                  tone="slate"
                />
              </DetailBlock>
              <DetailBlock title={`Rasy (${data.herd.by_breed.length})`}>
                <BarList
                  items={data.herd.by_breed}
                  empty="Brak przypisanych ras."
                  tone="green"
                />
              </DetailBlock>
            </div>
          </StatSection>

          <StatSection
            title="Rozród"
            summary="Wykoty i stanówki"
            tone="repro"
            open
          >
            <div className="stats-scope-bar">
              <p className="stats-scope-bar__label">Zakres: {scopeLabel}</p>
              <div className="stats-scope" role="group" aria-label="Zakres sezonów">
                {(
                  [
                    ["all", "Wszystkie"],
                    ["active", "Aktywne"],
                    ["archived", "Archiwum"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={
                      seasonScope === value ? "btn btn--sm" : "btn btn--ghost btn--sm"
                    }
                    aria-pressed={seasonScope === value}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setScope(value);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="stats-repro-grid">
              <div className="stats-repro-block">
                <h3 className="stats-repro-block__title">Wykoty</h3>
                <div className="stats-mini-grid stats-mini-grid--2">
                  <MiniStat
                    label="Sezony"
                    value={lambingView.seasonCount}
                    note={scopeLabel}
                    tone="ok"
                  />
                  <MiniStat label="Wykoty" value={lambingView.eventCount} />
                  <MiniStat
                    label="Jagnięta"
                    value={lambingView.lambCount}
                    note={`samice ${lambingView.femaleLambs} · samce ${lambingView.maleLambs}`}
                  />
                  <MiniStat
                    label="Śr. miot"
                    value={formatNum(lambingView.avgLitter)}
                    tone="info"
                  />
                </div>
                {lambingView.missingOfficial > 0 ? (
                  <p className="stats-callout stats-callout--warn">
                    Bez oficjalnego kolczyka: {lambingView.missingOfficial}
                  </p>
                ) : null}
                <DetailBlock title="Tabela sezonów wykotów" open>
                  {lambingSeasons.length === 0 ? (
                    <p className="muted">Brak sezonów w wybranym zakresie.</p>
                  ) : (
                    <div className="table-wrap">
                      <ResponsiveTable className="tbl">
                        <thead>
                          <tr>
                            <th>Sezon</th>
                            <th>Status</th>
                            <th>Wykoty</th>
                            <th>Jagnięta</th>
                            <th>Samice / samce</th>
                            <th>Śr. miot</th>
                            <th>Bez kolczyka</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lambingSeasons.map((season) => (
                            <tr key={season.id}>
                              <td>
                                <strong>{season.name}</strong>
                                {season.breed ? (
                                  <span className="muted"> · {season.breed}</span>
                                ) : null}
                              </td>
                              <td>
                                <span
                                  className={
                                    season.archived
                                      ? "stats-badge stats-badge--muted"
                                      : "stats-badge stats-badge--ok"
                                  }
                                >
                                  {season.archived ? "Archiwum" : "Aktywny"}
                                </span>
                              </td>
                              <td>{season.event_count}</td>
                              <td>{season.offspring_count}</td>
                              <td>
                                {season.female_offspring} / {season.male_offspring}
                              </td>
                              <td>{formatNum(season.avg_litter_size)}</td>
                              <td>{season.missing_official_tags}</td>
                            </tr>
                          ))}
                        </tbody>
                      </ResponsiveTable>
                    </div>
                  )}
                </DetailBlock>
              </div>

              <div className="stats-repro-block">
                <h3 className="stats-repro-block__title">Stanówki</h3>
                <div className="stats-mini-grid stats-mini-grid--2">
                  <MiniStat
                    label="Sezony"
                    value={breedingView.seasonCount}
                    note={scopeLabel}
                    tone="ok"
                  />
                  <MiniStat label="Owce łącznie" value={breedingView.eweCount} />
                  <MiniStat label="Tryki łącznie" value={breedingView.ramCount} />
                  <MiniStat
                    label="Śr. pokrycie"
                    value={formatPct(breedingView.avgCoverage)}
                    tone="info"
                  />
                </div>
                <StatHint id="coverage" />
                <DetailBlock title="Tabela sezonów stanówki" open>
                  {breedingSeasons.length === 0 ? (
                    <p className="muted">Brak sezonów w wybranym zakresie.</p>
                  ) : (
                    <div className="table-wrap">
                      <ResponsiveTable className="tbl">
                        <thead>
                          <tr>
                            <th>Sezon</th>
                            <th>Status</th>
                            <th>Owce</th>
                            <th>Tryki</th>
                            <th>Pokrycia</th>
                            <th>Pokrycie %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {breedingSeasons.map((season) => (
                            <tr key={season.id}>
                              <td>
                                <strong>{season.name}</strong>
                                {season.breed ? (
                                  <span className="muted"> · {season.breed}</span>
                                ) : null}
                              </td>
                              <td>
                                <span
                                  className={
                                    season.archived
                                      ? "stats-badge stats-badge--muted"
                                      : "stats-badge stats-badge--ok"
                                  }
                                >
                                  {season.archived ? "Archiwum" : "Aktywny"}
                                </span>
                              </td>
                              <td>{season.dam_count}</td>
                              <td>{season.sire_count}</td>
                              <td>{season.mated_count}</td>
                              <td>{formatPct(season.mating_coverage_pct)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </ResponsiveTable>
                    </div>
                  )}
                </DetailBlock>
              </div>
            </div>
          </StatSection>

          <StatSection
            title="Odejścia ze stada"
            summary={`${data.exits.total} szt. w archiwum`}
            tone="exits"
            open={data.exits.total > 0}
          >
            <div className="stats-mini-grid">
              <MiniStat label="Sprzedane" value={data.exits.sold} tone="info" />
              <MiniStat label="Padłe" value={data.exits.dead} tone="danger" />
              <MiniStat label="Ubój" value={data.exits.slaughtered} tone="warn" />
              <MiniStat label="Razem" value={data.exits.total} />
            </div>
            <StatHint id="exits" />
            <div className="stats-detail-stack">
              <DetailBlock title="Wg powodu" open={data.exits.total > 0}>
                <BarList
                  items={data.exits.by_reason}
                  empty="Brak odejść."
                  tone="amber"
                />
              </DetailBlock>
              <DetailBlock title="Wg roku">
                <BarList
                  items={data.exits.by_year}
                  empty="Brak dat odejścia."
                  tone="slate"
                />
              </DetailBlock>
              <DetailBlock title="Wg płci">
                <BarList items={data.exits.by_sex} empty="Brak danych." tone="rose" />
              </DetailBlock>
            </div>
          </StatSection>

          <StatSection
            title="Kolczyki oficjalne"
            summary={`${data.tags.free_official_tags} wolnych z ${data.tags.total_official_tags}`}
            tone="tags"
          >
            <div className="stats-mini-grid">
              <MiniStat
                label="Wolne"
                value={data.tags.free_official_tags}
                tone="ok"
              />
              <MiniStat
                label="Przypisane"
                value={data.tags.assigned_official_tags}
              />
              <MiniStat label="W puli" value={data.tags.total_official_tags} />
            </div>
            <StatHint id="tags" />
            <p className="stats-footer-link">
              <AppLink href="/resources" className="btn--link">
                Zarządzaj kolczykami →
              </AppLink>
            </p>
          </StatSection>
        </div>
      ) : null}
    </main>
  );
}
